/** How a settings field is edited and sent. */
export type FieldKind = "text" | "email" | "url" | "list" | "number";

export interface FieldSpec {
  key: string;
  label: string;
  kind: FieldKind;
  /** Short help under the field. */
  hint?: string;
  placeholder?: string;
  /** Suffix for numbers ("ms", "%"). */
  unit?: string;
}

export interface FieldGroup {
  title: string;
  fields: FieldSpec[];
}

export const PAYEE_GROUPS: FieldGroup[] = [
  {
    title: "Business",
    fields: [
      { key: "name", label: "Payee name", kind: "text", hint: "Shown as who the client pays." },
      { key: "address", label: "Address", kind: "text" },
      { key: "gstin", label: "GSTIN", kind: "text", placeholder: "29ABCDE1234F1Z5" },
      {
        key: "supportEmail",
        label: "Billing support email",
        kind: "email",
        hint: "Clients' replies to reminders and receipts go here.",
      },
    ],
  },
  {
    title: "Bank transfer",
    fields: [
      { key: "bankAccountName", label: "Account name", kind: "text" },
      { key: "bankAccountNumber", label: "Account number", kind: "text" },
      { key: "bankIfsc", label: "IFSC", kind: "text", placeholder: "HDFC0001234" },
      { key: "bankName", label: "Bank", kind: "text" },
    ],
  },
  {
    title: "UPI and policies",
    fields: [
      { key: "upiId", label: "UPI ID", kind: "text", placeholder: "name@bank" },
      { key: "termsUrl", label: "Terms link", kind: "url", placeholder: "https://" },
      { key: "privacyUrl", label: "Privacy link", kind: "url", placeholder: "https://" },
      { key: "refundsUrl", label: "Refund policy link", kind: "url", placeholder: "https://" },
    ],
  },
];

export const NOTIFICATION_GROUPS: FieldGroup[] = [
  {
    title: "Email",
    fields: [
      {
        key: "alertEmails",
        label: "Read-only alerts go to",
        kind: "list",
        hint: "Emailed when a client's TeleOpd becomes read-only. Separate addresses with commas.",
        placeholder: "ops@metahos.com, billing@metahos.com",
      },
      {
        key: "emailFromName",
        label: "Sender name",
        kind: "text",
        hint: "The address stays billing@metahos.com (the domain SendGrid is set up for).",
      },
    ],
  },
  {
    title: "WhatsApp templates",
    fields: [
      {
        key: "whatsappReminderTemplate",
        label: "Reminder template",
        kind: "text",
        hint: "Name of the Meta-approved template, e.g. sap_payment_reminder. Empty: no WhatsApp reminders.",
      },
      {
        key: "whatsappReceiptTemplate",
        label: "Receipt template",
        kind: "text",
        hint: "e.g. sap_payment_receipt. Empty: no WhatsApp receipts.",
      },
    ],
  },
];

export const DASHBOARD_GROUPS: FieldGroup[] = [
  {
    title: "Limits",
    fields: [
      {
        key: "slowResponseMs",
        label: "Slow response",
        kind: "number",
        unit: "ms",
        hint: "A tenant answering slower than this shows as degraded.",
      },
      {
        key: "usageWarnPct",
        label: "Warning level",
        kind: "number",
        unit: "%",
        hint: "CPU, memory and disk bars turn amber here.",
      },
      {
        key: "usageCriticalPct",
        label: "Critical level",
        kind: "number",
        unit: "%",
        hint: "…and red here.",
      },
    ],
  },
];
